import { Arrow, DataGlyph } from "../components/Brand";
import { PageMeta } from "../components/PageMeta";
import { CTASection, PageHero, SectionHeading } from "../components/Sections";
import { capabilities } from "../data/site";
import { useI18n, useLocalizedContent } from "../i18n";

const layers = [
  {
    label: "AI 应用",
    detail: "Agent · Knowledge · Workflow · Multimodal",
  },
  { label: "云与 IoT", detail: "Model Service · Device Service · Data" },
  {
    label: "端侧智能",
    detail: "Edge Runtime · Sensor Fusion · Control",
  },
  { label: "嵌入式系统", detail: "SoC / MCU · BSP · Firmware · Drivers" },
  {
    label: "硬件产品",
    detail: "Architecture · PCB · Structure · Interaction",
  },
  {
    label: "产品化",
    detail: "Prototype · Test · Certification · Production",
  },
];

const principles = [
  ["RELIABLE", "可靠", "失败可恢复、行为可预测、关键路径有明确的兜底机制。"],
  [
    "OBSERVABLE",
    "可观测",
    "从输入、检索、推理到工具执行，每一步都有完整轨迹。",
  ],
  [
    "MANUFACTURABLE",
    "可制造",
    "从器件、PCB、结构到测试工装，都面向稳定生产与成本目标设计。",
  ],
  [
    "EVOLVABLE",
    "可演进",
    "模型、应用、固件与设备都拥有清晰的升级、维护与版本边界。",
  ],
];

export function CapabilitiesPage() {
  const { l, locale } = useI18n();
  const localizedCapabilities = useLocalizedContent(capabilities);
  const localizedLayers = useLocalizedContent(layers);
  const localizedPrinciples = useLocalizedContent(principles);
  return (
    <>
      <PageMeta title={l("服务能力", "Services")} />
      <PageHero
        eyebrow={l("服务能力", "SERVICES")}
        title={
          <>
            {l("从工作原型，到", "From working prototype to ")}
            <span className="blue-text">
              {l("规模化产品。", "product at scale.")}
            </span>
          </>
        }
        lead={l(
          "覆盖 AI 软件、SaaS、云与设备服务、智能硬件、软硬件系统，以及从发布到量产和长期运维的完整产品工程。",
          "AI software, SaaS, cloud and device services, smart hardware, integrated systems, and the engineering needed to launch, manufacture, and operate them.",
        )}
      />

      <section className="section business-lines-section">
        <div className="container">
          <SectionHeading
            eyebrow={l("两类产品", "TWO PRODUCT WORLDS")}
            title={l(
              "纯软件，或进入现实世界的设备。",
              "Pure software, or devices in the real world.",
            )}
            description={l(
              "可以分别交付，也可以组合成一套从云到设备的完整产品。",
              "Delivered independently or combined into one complete cloud-to-device product.",
            )}
          />
          <div className="business-lines">
            <article className="business-line business-line--software">
              <div className="business-line__head">
                <span>{l("AI 软件 / SaaS", "AI SOFTWARE / SAAS")}</span>
                <strong>
                  {l("AI 软件与 SaaS 产品", "AI Software & SaaS Products")}
                </strong>
              </div>
              <p>
                {l(
                  "从产品设计、Agent 与企业知识，到完整 Web 应用、平台、API 和云端运维。",
                  "From product design, agents, and enterprise knowledge to complete web applications, platforms, APIs, and cloud operations.",
                )}
              </p>
              <div>
                {(locale === "zh"
                  ? [
                      "AI / SaaS 产品",
                      "Agent / RAG",
                      "多模态系统",
                      "云端与运维",
                    ]
                  : [
                      "AI / SaaS products",
                      "Agents / RAG",
                      "Multimodal systems",
                      "Cloud & operations",
                    ]
                ).map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </article>
            <div className="business-lines__bridge">
              <span>AIoT</span>
              <Arrow />
              <small>{l("云 · 边 · 端", "CLOUD · EDGE · DEVICE")}</small>
            </div>
            <article className="business-line business-line--hardware">
              <div className="business-line__head">
                <span>{l("智能硬件", "SMART HARDWARE")}</span>
                <strong>
                  {l("智能硬件与 AIoT 产品", "Smart Hardware & AIoT Products")}
                </strong>
              </div>
              <p>
                {l(
                  "从电子方案、固件和结构样机，到连接云端、可测试并可量产的产品。",
                  "From electronics, firmware, and mechanical prototypes to connected, testable, manufacturable products.",
                )}
              </p>
              <div>
                {(locale === "zh"
                  ? ["硬件方案 / PCB", "嵌入式固件", "结构与整机", "打样与量产"]
                  : [
                      "Electronics / PCB",
                      "Embedded firmware",
                      "Mechanical / enclosure",
                      "Prototype / production",
                    ]
                ).map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="section architecture-section">
        <div className="container">
          <SectionHeading
            eyebrow={l("产品架构", "PRODUCT ARCHITECTURE")}
            title={l(
              "按产品目标组合技术，而不是被技术栈绑定。",
              "Technology follows the product—not the other way around.",
            )}
            description={l(
              "根据场景、成本、性能和供应链选择模型、软件平台、芯片与设备方案。",
              "Models, software platforms, chips, and device architectures are selected around use case, cost, performance, and supply chain.",
            )}
          />
          <div className="layer-stack">
            {localizedLayers.map((layer, index) => (
              <div className="layer-row" key={layer.label}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <strong>{layer.label}</strong>
                <p>{layer.detail}</p>
                <Arrow />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section capability-catalog">
        <div className="container">
          <SectionHeading
            eyebrow={l("工程能力", "ENGINEERING CAPABILITIES")}
            title={l(
              "支撑完整产品的专业能力。",
              "Specialist capabilities behind a complete product.",
            )}
          />
          <div className="catalog-grid">
            {localizedCapabilities.map((item, index) => (
              <article id={item.id} className="catalog-card" key={item.id}>
                <div className="catalog-card__head">
                  <span>{item.code}</span>
                  <span>
                    {
                      {
                        Model: l("模型", "Model"),
                        Agent: "Agent",
                        Application: l("应用", "Application"),
                        Infrastructure: l("基础设施", "Infrastructure"),
                        Edge: l("边缘", "Edge"),
                        Hardware: l("硬件", "Hardware"),
                        Productization: l("产品化", "Productization"),
                      }[item.group]
                    }
                  </span>
                </div>
                <DataGlyph variant={index} />
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <div className="catalog-card__line">
                  <i />
                  <i />
                  <i />
                  <i />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section dark-section engineering-section">
        <div className="container">
          <SectionHeading
            inverse
            eyebrow={l("工程原则", "ENGINEERING PRINCIPLES")}
            title={l(
              "Demo 要快，产品要可靠。",
              "Move fast in the demo. Engineer rigorously for the product.",
            )}
          />
          <div className="engineering-grid">
            {localizedPrinciples.map(([, title, text], index) => (
              <article key={title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <CTASection />
    </>
  );
}
